'use client';

/**
 * Il profilo tipico GSE di un punto di misura.
 *
 * Quello che serve a chi guarda è capire quale forma sta vedendo nella curva e
 * su quali numeri è stata scalata — e, quando i numeri sono un default, che è un
 * default: è l'unica differenza fra «il socio consuma 2.700 kWh» e «non ce l'ha
 * ancora detto».
 *
 * ── Perché adesso è anche una tendina ───────────────────────────────────────
 * Il codice resta **dedotto** da ruolo, tipo di socio e accumulo, e per la quasi
 * totalità dei punti la deduzione basta. Ma due cose non le sa fare: non
 * raggiunge `PIRM` — l'infrastruttura di ricarica veicoli non è desumibile da
 * nessun campo dell'anagrafica — e non sa correggersi dove la tipologia del
 * socio è un'approssimazione di quella vera del Registro Centrale.
 *
 * Da qui la tendina, che però non nasconde la deduzione: mostra sempre quale
 * codice sarebbe stato dedotto e offre il ritorno a quello. Senza `options` la
 * card resta in sola lettura, com'era.
 */

import { IT_INT } from '@slims/shared/constants';
import { RotateCcw } from 'lucide-react';
import { Button } from '../../atoms/button/button';
import { DetailSelectInput } from '../detail-fields/detail-fields';

export type TypicalProfileBasis = 'dichiarato' | 'default_tipo' | 'impianti' | 'default_kwp';

export interface TypicalProfileOption {
	code: string;
	label: string;
}

export interface TypicalProfileCardProps {
	/** Presentazione senza una seconda card, quando è già nel profilo del punto. */
	embedded?: boolean;
	/** Codice GSE in uso, es. `PDMM`. Già risolto: scelto a mano se c'è, dedotto altrimenti. */
	codice: string;
	/** Cosa descrive il codice, dal registro condiviso. */
	label: string;
	/** La forma della giornata, in una riga. */
	shape: string;
	/** Somma dei dodici totali mensili distribuiti dal profilo, in kWh. */
	annualKwh: number;
	basis: TypicalProfileBasis;
	/** I campi da cui il codice è stato dedotto, già in forma leggibile. */
	derivedFrom: { label: string; value: string }[];
	/** I profili scegliibili per il verso di questo punto. Assente = sola lettura. */
	options?: TypicalProfileOption[];
	/** Il codice scelto a mano, o `null` se il punto segue la deduzione. */
	selected?: string | null;
	/** Il codice che la deduzione produrrebbe, mostrato quando se ne è scelto un altro. */
	derivedCode?: string;
	/** Sceglie un codice, o torna al dedotto con `null`. */
	onSelect?: (code: string | null) => void;
	/** Blocca la tendina durante un salvataggio. */
	disabled?: boolean;
}

const BASIS_TEXT: Record<
	TypicalProfileBasis,
	{ label: string; hint: string; provvisorio: boolean }
> = {
	dichiarato: {
		label: 'consumo dichiarato dal socio',
		hint: 'Dal questionario consumi.',
		provvisorio: false,
	},
	default_tipo: {
		label: 'media per tipo di socio',
		hint: 'Il socio non ha ancora dichiarato il consumo annuo: finché non lo fa, la curva usa la media del suo tipo.',
		provvisorio: true,
	},
	impianti: {
		label: 'kWp degli impianti censiti',
		hint: 'Potenza installata × resa mensile PVGIS.',
		provvisorio: false,
	},
	default_kwp: {
		label: 'potenza di riferimento',
		hint: 'Nessun impianto censito su questo punto: la curva usa una taglia di riferimento, e sparisce al primo impianto inserito.',
		provvisorio: true,
	},
};

export function TypicalProfileCard({
	codice,
	label,
	shape,
	annualKwh,
	basis,
	derivedFrom,
	options,
	selected = null,
	derivedCode,
	onSelect,
	disabled,
	embedded = false,
}: TypicalProfileCardProps) {
	const info = BASIS_TEXT[basis];
	// Scelto a mano *e diverso* dal dedotto: chi seleziona esattamente il codice
	// che sarebbe uscito comunque non ha creato un'eccezione da segnalare.
	const overridden = selected != null && selected !== derivedCode;

	return (
		<div
			className={
				embedded
					? 'flex flex-col gap-2.5'
					: 'flex flex-col gap-2.5 rounded-lg border border-border-secondary bg-bg-secondary p-3'
			}
		>
			{options && onSelect ? (
				<div className="flex flex-col gap-1.5">
					<DetailSelectInput
						aria-label="Codice di profilo GSE"
						value={selected ?? ''}
						onChange={(v: string) => onSelect(v === '' ? null : v)}
						options={options.map((o) => ({ value: o.code, label: `${o.code} · ${o.label}` }))}
						placeholder={
							derivedCode ? `Dedotto automaticamente (${derivedCode})` : 'Dedotto automaticamente'
						}
						disabled={disabled}
					/>
					{overridden && (
						<div className="flex flex-wrap items-center gap-2 text-[11px] text-warn">
							<span>Scelto a mano{derivedCode ? ` · il dedotto sarebbe ${derivedCode}` : ''}</span>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								onClick={() => onSelect(null)}
								disabled={disabled}
							>
								<RotateCcw />
								Torna al dedotto
							</Button>
						</div>
					)}
				</div>
			) : (
				<div className="flex flex-wrap items-baseline gap-2">
					<span className="rounded-md border border-accent/30 bg-accent/10 px-1.5 py-0.5 font-mono font-semibold text-[13px] text-accent">
						{codice}
					</span>
					<span className="font-semibold text-fg text-sm">{label}</span>
				</div>
			)}

			<p className="text-[12px] text-fg-secondary">{shape}</p>

			<div className="flex flex-wrap items-baseline gap-1.5 text-[12px]">
				<span className="text-fg-secondary">Energia distribuita:</span>
				<span className="font-semibold text-fg tabular-nums">
					{Math.round(annualKwh).toLocaleString('it-IT', IT_INT)} kWh/anno
				</span>
				<span className={info.provvisorio ? 'text-warn' : 'text-fg-secondary'}>· {info.label}</span>
			</div>

			{info.provvisorio && <p className="text-[11px] text-warn">{info.hint}</p>}

			{derivedFrom.length > 0 && (
				<dl className="flex flex-wrap gap-x-4 gap-y-1 border-border-secondary border-t pt-2 text-[11px]">
					{derivedFrom.map((row) => (
						<div key={row.label} className="flex gap-1">
							<dt className="text-fg-secondary">{row.label}:</dt>
							<dd className="text-fg">{row.value}</dd>
						</div>
					))}
				</dl>
			)}
		</div>
	);
}
